/**
 * Reusable MUI confirmation dialog (issue #255 AC2).
 *
 * Replaces every `window.confirm(...)` call site in the admin SPA. The
 * native browser confirm dialog is jarring (OS-dependent styling, blocks
 * the JS thread, no theming) and inconsistent with the rest of the admin
 * which already uses MUI `<Dialog>` for affirmative actions (publish
 * dialog, create-experiment dialog).
 *
 * Usage — one mounted dialog per page, called like a promise:
 *
 *     const { confirm, dialogElement } = useConfirmDialog();
 *     // ...
 *     const handleUnpublish = async () => {
 *       if (!(await confirm({
 *         title: "Unpublish this version?",
 *         body: "The public site will no longer render it.",
 *         confirmLabel: "Unpublish",
 *       }))) return;
 *       unpublish.mutate(...);
 *     };
 *     return <>{dialogElement}{actualUI}</>;
 *
 * The hook's `confirm()` returns a Promise<boolean>. The boolean is
 * `true` on the confirm button click, `false` on cancel (or Esc / backdrop
 * click). Multiple concurrent `confirm()` calls are NOT supported — the
 * second resolves to `false` immediately so a caller never accidentally
 * acts on a stale prompt's response.
 *
 * testIDs (consumed by Playwright):
 *   - `confirm-dialog`            — the wrapper Dialog
 *   - `confirm-dialog-confirm`    — the affirmative button
 *   - `confirm-dialog-cancel`     — the cancel button
 *
 * MUI only.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

export type ConfirmDialogOptions = {
  title: string;
  body: ReactNode;
  /** Default: "Confirm". */
  confirmLabel?: string;
  /** Default: "Cancel". */
  cancelLabel?: string;
  /**
   * Color of the affirmative button. Defaults to `"error"` because the
   * overwhelming majority of call sites in this codebase are destructive
   * operations (unpublish, archive, delete-variant, member-remove). A
   * non-destructive caller can pass `"primary"`.
   */
  confirmColor?: "primary" | "error" | "warning";
};

type PendingState = {
  options: ConfirmDialogOptions;
  resolve: (value: boolean) => void;
};

/**
 * Hook returns the `confirm()` function (Promise-based) and the
 * `dialogElement` that the caller must mount once in their component
 * tree. Mounting the element exactly once per page keeps the dialog's
 * open/close state co-located with the call site and avoids the
 * complexity (and SSR pitfalls) of a global context-mounted dialog.
 */
export function useConfirmDialog(): {
  confirm: (opts: ConfirmDialogOptions) => Promise<boolean>;
  dialogElement: ReactElement;
} {
  const [pending, setPending] = useState<PendingState | null>(null);

  // Hold the latest pending state's resolver in a ref so the Dialog's
  // event handlers see the current value even if React batches an
  // update. Without this, a click on "Confirm" immediately after the
  // dialog opens can race the `setPending` from the `confirm()` call
  // and resolve `undefined`.
  const pendingRef = useRef<PendingState | null>(null);
  pendingRef.current = pending;

  const confirm = useCallback((opts: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      // If a previous confirm() is still mid-flight, resolve it as
      // cancelled before replacing. This prevents the previous caller
      // from ever seeing a hung promise.
      const previous = pendingRef.current;
      if (previous) {
        previous.resolve(false);
      }
      setPending({ options: opts, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    const current = pendingRef.current;
    if (current) {
      current.resolve(true);
    }
    setPending(null);
  }, []);

  const handleCancel = useCallback(() => {
    const current = pendingRef.current;
    if (current) {
      current.resolve(false);
    }
    setPending(null);
  }, []);

  const dialogElement = useMemo(() => {
    const open = pending !== null;
    const opts = pending?.options ?? null;
    const confirmLabel = opts?.confirmLabel ?? "Confirm";
    const cancelLabel = opts?.cancelLabel ?? "Cancel";
    const confirmColor = opts?.confirmColor ?? "error";
    return (
      <Dialog
        open={open}
        onClose={handleCancel}
        data-testid="confirm-dialog"
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">{opts?.title ?? ""}</DialogTitle>
        <DialogContent>
          {/* `DialogContentText` renders as a `<p>`; wrap non-string
              bodies in a fragment so callers can pass e.g. <Stack>. */}
          {typeof opts?.body === "string" ? (
            <DialogContentText>{opts.body}</DialogContentText>
          ) : (
            (opts?.body ?? null)
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} data-testid="confirm-dialog-cancel">
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            color={confirmColor}
            variant="contained"
            data-testid="confirm-dialog-confirm"
            autoFocus
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [pending, handleConfirm, handleCancel]);

  return { confirm, dialogElement };
}
